import {WixSdk} from '@wix/js-sdk-wrapper';
import {StatisticParams} from '@wix/music-player';
import {getRandomGuid4} from '@wix/music-shared/dist/src/utils/wix-utils';
import {getAppVersion} from '@wix/music-shared/dist/src/wmp-shared/utils/env/getAppVersion';
import {EventEmitter} from 'events';
import {playerVersion} from '../logging/playerVersion';
import {sessionId} from '../logging/session';
import {noop} from '../utils/functions';
import {biErrorLogger, biLogger} from './_loggers';
import {getWixParams} from './getWixParams';
import {LogPlayerEventsOptions} from './send-event';
import {subscribeToEmitter} from './subscribe-to-emitter';

export interface BiErrorParams {
  p1?: string;
  p2?: string;
  p3?: string;
  p4?: string;
}

export interface ErrorDescription {
  id: number;
  name: string;
  severity?: number;
}

export function reportBiError(errorDescription: ErrorDescription, errorParams: BiErrorParams = {}) {
  const params = {
    src: 9,
    evid: 10,
    cat: 1,
    iss: 1,
    errc: errorDescription.id,
    errn: errorDescription.name,
    sev: errorDescription.severity,
    sessionId,
    artifactVersion: getAppVersion(),
    playerVersion,
    ...errorParams,
  };

  biErrorLogger.log(params);
}

let playerStartedFired = false;

export function logToBi(statisticsEvents: EventEmitter, {isOwner, compId}: LogPlayerEventsOptions) {
  let trackCorrelationId: string;

  const events = {
    playerStarted: {
      id: 189,
      name: 'player.pageOpened',
      params({album, loading_time, isAutoplay, layout}: StatisticParams) {
        const layoutNames = {'-1': 'mobile', 0: 'vertical', 1: 'horizontal', 2: 'list'};
        const layoutName = layoutNames[layout as keyof typeof layoutNames];
        return {
          albumID: album.id,
          loading_time,
          isDemoAlbum: Boolean(album.demo),
          layout: layoutName,
          viewMode: WixSdk.Utils.getViewMode(),
          isAutoplay,
        };
      },
    },
    trackPlayStart: {
      id: 190,
      name: 'player.track.played',
      params: (args: StatisticParams) => ({
        ...baseTrackData(args),
        correlationId: getTrackCorrelationId(),
        isAutoplay: args.isAutoplay,
      }),
    },
    trackSkipped: {
      id: 191,
      name: 'player.track.skipped',
      params: baseTrackData,
    },
    trackPlayCompleted: {
      id: 192,
      name: 'player.track.completed',
      params: (args: StatisticParams) => ({
        ...baseTrackData(args),
        correlationId: getTrackCorrelationId(true),
      }),
    },
    trackPurchaseClicked: {
      id: 194,
      name: 'player.track.purchase.clicked',
      params: (args: StatisticParams) => ({
        ...baseTrackData(args),
        price: (args.track.trackPrice * 10000).toFixed(0),
        currency: args.album.currency,
        origin: args.origin,
        sendToDownloadPage: args.sendToDownloadPage,
      }),
    },
    trackShareClicked: {
      id: 202,
      name: 'player.track.shared',
      params: (args: StatisticParams) => ({
        ...baseTrackData(args),
        socialNetwork: args.network,
      }),
    },
    albumPlayCompleted: {
      id: 196,
      name: 'player.album.playComplete',
      params: baseAlbumData,
    },
    albumPurchaseClicked: {
      id: 198,
      name: 'player.album.purchase.clicked',
      params: (args: StatisticParams) => ({
        ...baseAlbumData(args),
        price: (args.album.albumPrice * 10000).toFixed(0),
        currency: args.album.currency,
        origin: args.origin,
        sendToDownloadPage: args.sendToDownloadPage,
      }),
    },
    albumShareClicked: {
      id: 200,
      name: 'player.album.shared',
      params: (args: StatisticParams) => ({
        ...baseAlbumData(args),
        socialNetwork: args.network,
      }),
    },
    playerAudioFailed: {
      id: 205,
      name: 'player.audio.failed',
      params: baseTrackData,
    },
    trackPlayAttempt: {
      id: 207,
      name: 'player.track.play.attempt',
      params: (args: StatisticParams) => ({
        ...baseTrackData(args),
        isAutoplay: args.isAutoplay,
      }),
    },
    viewTrackPage: {
      id: 208,
      name: 'player.track.viewTrackPage.clicked',
      params: (args: StatisticParams) => ({
        ...baseTrackData(args),
        isOwner,
      }),
    },
    trackTimePlayed: {
      id: 227,
      name: 'player.track.progress',
      params: (args: StatisticParams) => ({
        ...baseTrackData(args),
        correlationId: getTrackCorrelationId(true),
        timePlayed: args.playedTime,
      }),
    },
  };

  subscribeToEmitter(statisticsEvents, events, (eventName, eventDescription, eventArgs) => {
    const viewMode = WixSdk.Utils.getViewMode();
    const artifactVersion = getAppVersion();
    const isFirstPlayerStartEvent = eventName === 'playerStarted' && !playerStartedFired;
    if (!isFirstPlayerStartEvent && (viewMode === 'editor' || viewMode === 'preview')) {
      return;
    }
    if (isFirstPlayerStartEvent) {
      playerStartedFired = true;
    }

    const params = {
      src: 9,
      evid: eventDescription.id,
      compId,
      isOwner,
      sessionId,
      playerVersion,
      artifactVersion,
      ...(eventDescription.params || noop)(eventArgs),
    };
    void biLogger.log(params);
  });

  function baseAlbumData({album}: StatisticParams) {
    return {
      albumID: album.id,
    };
  }

  function baseTrackData(params: StatisticParams) {
    const {album, track} = params;
    return {
      ...baseAlbumData(params),
      trackID: track.id,
      trackIdxInAlbum: album.tracks.indexOf(track),
    };
  }

  function getTrackCorrelationId(hasPlayEnded = false) {
    if (hasPlayEnded) {
      return trackCorrelationId;
    }

    trackCorrelationId = getRandomGuid4();

    return trackCorrelationId;
  }
}

export function sendPlayerStartedEvent() {
  const {compId, isOwner} = getWixParams();

  const params = {
    src: 9,
    evid: 189,
    compId,
    isOwner,
    albumID: getRandomGuid4(),
    loading_time: window.performance && window.performance.now ? window.performance.now() : null,
    sessionId,
  };

  void biLogger.log(params);
}
